import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';

import { ApiService } from '@mm-aggregator/drf-dynamics';

/**
 * API Provider model from the backend.
 */
export interface APIProvider {
  id: string;
  code: string;
  name: string;
  provider_type: string;
  adapter_class: string | null;
  configuration: Record<string, unknown>;
  is_active: boolean;
  financial_entity: string | null;
  financial_entity_code: string | null;
  financial_entity_name: string | null;
  country: string | null;
  country_code: string | null;
  country_name: string | null;
  currency: string | null;
  currency_code: string | null;
  route_count: number;
  secret_keys: string[];
  webhook_url: string;
  created_at: string;
  updated_at: string;
}

/**
 * Paginated list response.
 */
export interface PaginatedResponse<T> {
  count: number;
  next: string | null;
  previous: string | null;
  results: T[];
}

export interface ProviderHubCurrencyTotal {
  currency: string;
  count: number;
  amount: string;
}

export interface ProviderHubCurrencyStatusTotal {
  currency: string;
  status: string;
  count: number;
  amount: string;
}

export interface ProviderHubTransactionTypeCard {
  transaction_type: string;
  total_count: number;
  currency_totals: ProviderHubCurrencyTotal[];
  currency_status_totals: ProviderHubCurrencyStatusTotal[];
}

export interface ProviderHubResponse {
  provider_id: string;
  provider_code: string;
  transaction_type_cards: ProviderHubTransactionTypeCard[];
}

/** Daily chart point — aligned with dashboard DailyDataPoint. */
export interface ProviderHubDailyPoint {
  date: string;
  label: string;
  deposits_count: number;
  deposits_amount: number;
  payouts_count: number;
  payouts_amount: number;
  refunds_count: number;
  remittances_count: number;
}

/** Transaction type summary — aligned with dashboard TransactionTypeSummary. */
export interface ProviderHubTransactionType {
  name: string;
  value: number;
  amount?: number;
}

export interface ProviderHubChartsResponse {
  daily: ProviderHubDailyPoint[];
  transaction_types: ProviderHubTransactionType[];
  days: number;
}

/**
 * Service for managing API Providers.
 */
@Injectable({
  providedIn: 'root',
})
export class ProviderService {
  private readonly api = inject(ApiService);
  private readonly basePath = 'manage/api-providers';

  /**
   * List all providers with optional filters.
   */
  list(params?: Record<string, string | number | boolean | undefined>): Observable<PaginatedResponse<APIProvider>> {
    return this.api.get<PaginatedResponse<APIProvider>>(`${this.basePath}/`, params);
  }

  /**
   * Get a single provider by ID.
   */
  get(id: string): Observable<APIProvider> {
    return this.api.get<APIProvider>(`${this.basePath}/${id}/`);
  }

  /**
   * Create a new provider.
   */
  create(data: Partial<APIProvider>): Observable<APIProvider> {
    return this.api.post<APIProvider>(`${this.basePath}/`, data);
  }

  /**
   * Update a provider (partial).
   */
  update(id: string, data: Partial<APIProvider>): Observable<APIProvider> {
    return this.api.patch<APIProvider>(`${this.basePath}/${id}/`, data);
  }

  /**
   * Delete a provider.
   */
  delete(id: string): Observable<void> {
    return this.api.delete(`${this.basePath}/${id}/`);
  }

  /**
   * Activate a provider.
   */
  activate(id: string): Observable<APIProvider> {
    return this.api.post<APIProvider>(`${this.basePath}/${id}/activate/`, {});
  }

  /**
   * Deactivate a provider.
   */
  deactivate(id: string): Observable<APIProvider> {
    return this.api.post<APIProvider>(`${this.basePath}/${id}/deactivate/`, {});
  }

  /**
   * Set encrypted secrets for a provider.
   */
  setSecrets(id: string, secrets: Record<string, string>): Observable<APIProvider> {
    return this.api.post<APIProvider>(`${this.basePath}/${id}/set-secrets/`, { secrets });
  }

  /**
   * Get aggregated provider hub metrics.
   */
  getHub(id: string): Observable<ProviderHubResponse> {
    return this.api.get<ProviderHubResponse>(`${this.basePath}/${id}/hub/`);
  }

  /**
   * Get provider-scoped chart data for the hub page.
   */
  getHubCharts(id: string, days = 30): Observable<ProviderHubChartsResponse> {
    return this.api.get<ProviderHubChartsResponse>(`${this.basePath}/${id}/hub/charts/`, { days });
  }
}
